/**
 * データ取得フック (基盤パターン)
 *
 * - useFetch: 読み取り系 (stale-while-revalidate cache 経由。steering 551 / T6)
 * - useMutation: 書き込み系 (コールバック関数を返すミューテーション。失敗は null)
 * - useThrowingMutation: 書き込み系 (失敗を投げ直す。理由の code で表示を分ける画面用)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { apiFetch, isForbiddenApiError } from '@/api/client';
import { useToast } from '@/hooks/useToast';
import * as fetchCache from '@/api/fetchCache';

/**
 * 読み取り系の汎用データ取得フック
 *
 * - url が null の場合はフェッチしない (条件付きフェッチ)
 * - url 変更時に自動再取得
 * - refetch() で手動再取得 (**常に force** = cache を迂回する)
 *
 * steering 551 / T6: 内部を `api/fetchCache` 経由にした。**返値 shape と `refetch` の
 * 参照同一性は変えない** (非 test だけで 65 files / 約 144 call sites があり、
 * `useRefetchOnOpen` や polling effect が `refetch` を dep に載せているため)。
 *
 * 変わるのは次の 2 点だけ:
 *   - 再訪時に cache が新しければ **spinner を出さず即描画**する
 *   - 同一 URL の同時 mount が **1 request に dedup** される
 *
 * **unmount では request を abort しない。** 所有者は cache entry 側で、購読者が 0 に
 * なってから猶予を置いて abort する (他 consumer の request を殺さないため)。
 */
export function useFetch<T>(url: string | null): {
  data: T | null;
  loading: boolean;
  /**
   * request が飛んでいる間 true (steering 618)。`loading` は cache があれば false のままなので、
   * 「サーバの最新が返るまで操作を止めたい」画面はこちらを見る (再取得中・期限切れ後の再訪も含む)。
   */
  validating: boolean;
  /**
   * 表示中の data が **この url の最新** か (my-assigned-default-view-20261005)。cache が期限内か、
   * この hook が取得し終えたときだけ true。期限切れの cache を表示している間 (再取得の前後) は false。
   * 「結果を見て既定を自動で変える」処理が古い cache で判定しないために使う。既存の挙動は変えない。
   */
  fresh: boolean;
  error: string | null;
  refetch: () => void;
} {
  /**
   * 描画の正本は **local state**。cache はそれを前倒しで満たす accelerator として使う。
   *
   * cache から直接 render すると、identity scope が未確定のとき (認証 hydration 中や、
   * AuthProvider を張らない単体テスト) に `read()` が常に空を返し、fetch は成功しているのに
   * **データが永久に描画されない**。cache の有無に関わらず動く形にしておく。
   */
  const readSnapshot = (target: string): { data: T | null; error: string | null } => {
    const snapshot = fetchCache.read<T>(target);
    return { data: snapshot.data, error: snapshot.error };
  };

  const [state, setState] = useState<{ data: T | null; error: string | null }>(() =>
    url ? readSnapshot(url) : { data: null, error: null },
  );
  const [loading, setLoading] = useState<boolean>(() =>
    url ? !fetchCache.read<T>(url).hasData : false,
  );
  const [validating, setValidating] = useState(false);
  const [fresh, setFresh] = useState<boolean>(() =>
    url ? fetchCache.read<T>(url).isFresh : false,
  );
  // 再取得トリガー用カウンター (refetch の identity を変えないため ref + state の 2 本立て)。
  const retriggerRef = useRef(0);
  const [retrigger, setRetrigger] = useState(0);
  const lastHandledRetriggerRef = useRef(0);

  // url が変わったら、その url の snapshot を即座に反映する (前の url の値を残さない)。
  useEffect(() => {
    if (!url) {
      setState({ data: null, error: null });
      setLoading(false);
      setValidating(false);
      setFresh(false);
      return;
    }
    // readSnapshot は url だけに依存する純粋な読み出しなので、deps は url だけでよい。
    setState(readSnapshot(url));
  }, [url]);

  // cache entry の購読。unmount では購読解除だけを行う (**abort しない**)。
  useEffect(() => {
    if (!url) return;
    return fetchCache.subscribe(url, () => setState(readSnapshot(url)));
  }, [url]);

  useEffect(() => {
    if (!url) return;

    // `refetch()` 由来の実行か (url 変更だけなら force しない)。
    const force = retrigger !== lastHandledRetriggerRef.current;
    lastHandledRetriggerRef.current = retrigger;

    const snapshot = fetchCache.read<T>(url);
    // **データがあれば spinner を出さない** (これが AC-D1 / D3 の本体)。
    setLoading(!snapshot.hasData);

    // fresh かつ force でなければ request を出さない。
    if (snapshot.isFresh && !force) {
      setLoading(false);
      setValidating(false);
      setFresh(true);
      return;
    }

    let active = true;
    setValidating(true);
    setFresh(false);
    void fetchCache
      .fetchThrough<T>(url, (signal) => apiFetch<T>(url, { signal }), { force })
      .then((result) => {
        if (!active) return;
        // abort は null で返る (state を変えない = 現行 useApi と同じ)。
        if (result === null) return;
        setState({ data: result, error: null });
        setFresh(true);
      })
      .catch((err: unknown) => {
        if (!active) return;
        const message = err instanceof Error ? err.message : 'データの取得に失敗しました';
        // 失敗時は **data を捨てる** (古い数字を残さない)。
        setState({ data: null, error: message });
      })
      .finally(() => {
        if (active) {
          setLoading(false);
          setValidating(false);
        }
      });

    return () => {
      active = false;
    };
  }, [url, retrigger]);

  /** 手動でデータを再取得する (**常に force**: サーバの真値が欲しい意図のため)。 */
  const refetch = useCallback(() => {
    retriggerRef.current += 1;
    setRetrigger(retriggerRef.current);
  }, []);

  return { data: state.data, loading, validating, fresh, error: state.error, refetch };
}

/**
 * 書き込み系のミューテーションフック
 *
 * - fn を受け取り、mutate() で実行
 * - エラー時は useToast でエラートースト表示
 * - 成功時は result を返す、失敗時は null
 */
export function useMutation<TInput, TResult>(
  fn: (input: TInput) => Promise<TResult>,
): {
  mutate: (input: TInput) => Promise<TResult | null>;
  loading: boolean;
  error: string | null;
  /**
   * 直近の mutate の失敗理由 (成功・実行中は null)。`error` は state なので、`await mutate()` の直後に
   * 同じ描画の closure から読むと前回の値のまま。直後に理由で表示を分けたい呼び出し側はこちらを読む。
   */
  getLastError: () => string | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastErrorRef = useRef<string | null>(null);
  const getLastError = useCallback(() => lastErrorRef.current, []);
  const { showToast } = useToast();

  const mutate = useCallback(
    async (input: TInput): Promise<TResult | null> => {
      setLoading(true);
      setError(null);
      lastErrorRef.current = null;

      try {
        const result = await fn(input);
        return result;
      } catch (err) {
        const message = err instanceof Error ? err.message : '操作に失敗しました';
        setError(message);
        lastErrorRef.current = message;
        // 403 (権限不足) は client.ts が api:forbidden 経由でグローバルトーストを表示済。
        // ここで再度トーストすると二重表示になるため抑止する (トーストは 1 回にする)。
        if (!isForbiddenApiError(err)) {
          showToast('error', message);
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [fn, showToast],
  );

  return { mutate, loading, error, getLastError };
}

/**
 * 失敗を呼び出し側へ投げ直すミューテーションフック (bugfix-sweep-20260924)。
 *
 * `useMutation` は失敗を捕まえて **`null` を返す**ため、呼び出し側が失敗の理由
 * (`ApiResponseError.code`: 競合 / 見つからない など) で表示を分けられない。
 * `catch` で code を見て文言を出し分ける画面はこちらを使う。
 *
 * - エラートーストは `useMutation` と同じく出す (403 は client.ts のグローバルトーストに任せて出さない)
 * - そのうえで **同じ error を throw し直す** (成功時は結果を返し、`null` は返さない)
 */
export function useThrowingMutation<TInput, TResult>(
  fn: (input: TInput) => Promise<TResult>,
): {
  mutate: (input: TInput) => Promise<TResult>;
  loading: boolean;
  error: string | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();

  const mutate = useCallback(
    async (input: TInput): Promise<TResult> => {
      setLoading(true);
      setError(null);

      try {
        return await fn(input);
      } catch (err) {
        const message = err instanceof Error ? err.message : '操作に失敗しました';
        setError(message);
        if (!isForbiddenApiError(err)) {
          showToast('error', message);
        }
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [fn, showToast],
  );

  return { mutate, loading, error };
}
